import React, { useMemo, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation, gql } from '@apollo/client';
import { SaveIndicator } from '@personal-dashboard/ui';
import AddIcon from '@mui/icons-material/Add';
import { CreateWidgetFormFields } from './CreateWidgetFormFields';
import { SourceField } from '../types';
import {
  FieldType,
  ChartType,
  Aggregation,
  defaultChartTypeFor,
  defaultAggregationFor,
} from './widgetOptions';

const CREATE_DASHBOARD_WIDGET = gql`
  mutation CreateDashboardWidget($input: DashboardWidgetInput!) {
    createDashboardWidget(input: $input) {
      id
      title
      fieldId
      sectionId
      chartType
      aggregation
      layout {
        x
        y
        w
        h
        minW
        minH
      }
      visible
      orderIndex
    }
  }
`;

interface CreateWidgetFormValues {
  title: string;
  fieldId: string;
  chartType: ChartType;
  aggregation: Aggregation;
}

interface CreateWidgetFormProps {
  sourceFields: SourceField[];
  onCreated?: () => void;
}

const DEFAULT_LAYOUT = { x: 0, y: 0, w: 4, h: 4, minW: 2, minH: 2 };

export const CreateWidgetForm: React.FC<CreateWidgetFormProps> = ({ sourceFields, onCreated }) => {
  const [createWidget] = useMutation(CREATE_DASHBOARD_WIDGET, {
    refetchQueries: ['GetDashboardWidgets', 'GetDashboardWidgetsAndSections'],
  });
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const methods = useForm<CreateWidgetFormValues>({
    defaultValues: {
      title: '',
      fieldId: '',
      chartType: 'LINE' as ChartType,
      aggregation: 'LATEST' as Aggregation,
    },
  });

  const { control, watch, reset, setValue, handleSubmit } = methods;
  const selectedFieldId = watch('fieldId');

  const selectedField = useMemo(
    () => sourceFields.find((f) => f.fieldId === selectedFieldId),
    [sourceFields, selectedFieldId]
  );

  const fieldType: FieldType | undefined = selectedField?.fieldType;

  const handleFieldChange = (newFieldId: string) => {
    const field = sourceFields.find((f) => f.fieldId === newFieldId);
    if (field) {
      setValue('fieldId', newFieldId);
      setValue('chartType', defaultChartTypeFor(field.fieldType));
      setValue('aggregation', defaultAggregationFor(field.fieldType));
    }
  };

  const onSubmit = (values: CreateWidgetFormValues) => {
    if (!values.fieldId) return;
    setSaveStatus('saving');
    reset({ title: '', fieldId: '', chartType: 'LINE', aggregation: 'LATEST' });
    onCreated?.();
    createWidget({
      variables: {
        input: {
          title: values.title,
          fieldId: values.fieldId,
          chartType: values.chartType,
          aggregation: values.aggregation,
          layout: DEFAULT_LAYOUT,
        },
      },
    }).then(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }).catch(() => {
      setSaveStatus('error');
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <CreateWidgetFormFields
            sourceFields={sourceFields}
            fieldType={fieldType}
            onFieldChange={handleFieldChange}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!selectedFieldId}
            >
              Add to Library & Dashboard
            </Button>
            <SaveIndicator status={saveStatus} />
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default CreateWidgetForm;
